import { isResource } from '@medplum/core'
import type {
  Coding,
  Questionnaire,
  QuestionnaireItem,
} from '@medplum/fhirtypes'
import { flatMap } from 'lodash-es'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { canonical, isOfType } from '#src/utils.ts'

export class ExtendedQuestionnaire extends ExtendedResource<Questionnaire> {
  static readonly RESOURCE_TYPE = 'Questionnaire'

  constructor(data?: Omit<Questionnaire, 'resourceType'>) {
    super({
      status: 'active',
      ...data,
    })
  }

  get canonical(): string {
    const canonicalUrl = canonical(this.resource)
    invariant(
      canonicalUrl,
      `${this.reference.reference} must have a canonical URL`
    )
    return canonicalUrl
  }

  get tag(): Coding[] | undefined {
    return this.resource.meta?.tag
  }

  findItem(linkId: string): QuestionnaireItem | undefined {
    return this.flatItems.find((item) => item.linkId === linkId)
  }

  getItem(linkId: string): QuestionnaireItem {
    const item = this.findItem(linkId)
    invariant(item, `${this.reference.reference} missing item ${linkId}`)
    return item
  }

  private get flatItems(): QuestionnaireItem[] {
    return flatMap(this.resource.item, ExtendedQuestionnaire.flattenItem)
  }

  private static flattenItem(
    item: QuestionnaireItem | undefined
  ): QuestionnaireItem[] {
    if (!item) {
      return []
    }
    return item.item
      ? [item, ...flatMap(item.item, ExtendedQuestionnaire.flattenItem)]
      : [item]
  }

  getItemText(linkId: string): string {
    return this.getItem(linkId)?.text ?? `NOTE: Missing text for ${linkId}`
  }

  static isInstance(value: unknown): value is Questionnaire {
    return isResource(value) && isOfType(value, 'Questionnaire')
  }

  get status(): Questionnaire['status'] {
    return this.resource.status
  }

  get version(): string | undefined {
    return this.resource.version
  }

  get title(): string | undefined {
    return this.resource.title
  }

  get description(): string | undefined {
    return this.resource.description
  }

  get publisher(): string | undefined {
    return this.resource.publisher
  }

  get dateString(): string | undefined {
    return this.resource.date
  }

  get subjectType(): string[] | undefined {
    return this.resource.subjectType
  }

  get codeSummary(): string[] | undefined {
    return this.resource.code
      ?.map((c) => codeFromCodeableConcept(c))
      .filter((c) => c !== undefined)
  }

  get itemSummary(): string[] {
    return this.flatItems.map(
      (item) => `${item.linkId}: ${item.text} (${item.type})`
    )
  }

  get tagSummary(): string[] | undefined {
    return this.tag?.map((t) => `${t.system}: ${t.code}`)
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      url: this.canonical,
      version: this.version,
      title: this.title,
      description: this.description,
      publisher: this.publisher,
      date: this.dateString,
      subjectType: this.subjectType,
      code: this.codeSummary,
      items: this.itemSummary,
      tag: this.tagSummary,
    }
  }
}
